/**
 * 结果体显示名称所需的最小特征摘要。
 */
export interface ResultBodyDisplayNameFeatureSummary {
    /** 特征 ID。 */
    id: string;
    /** 特征种类。 */
    kind: string;
    /** 特征显示名。 */
    displayName: string;
}

/**
 * 构建结果体显示名称映射的输入。
 */
export interface BuildResultBodyDisplayNameMapInput {
    /** 当前状态中的几何对象列表。 */
    objects: GeometryObject[];
    /** 已投影好的特征摘要列表。 */
    features: ResultBodyDisplayNameFeatureSummary[];
    /** 宿主侧的显式重命名覆盖。 */
    objectNameOverrides: Map<string, string>;
}

/**
 * 结果体特征分组上下文。
 */
interface ResultBodyFeatureGroup {
    /** 特征 ID。 */
    featureId: string;
    /** 特征种类。 */
    featureKind: string;
    /** 该特征生成的结果体 ID 列表。 */
    objectIds: string[];
}

/**
 * 结果体显示名称策略。
 */
export class ResultBodyDisplayNamePolicy {
    /**
     * 构建结果体显示名称映射。
     *
     * 设计说明：
     * 1. 设计树中的结果体命名必须与特征命名同源，不能在 web-client 再次猜测；
     * 2. cut 特征产生多个结果体时，需要在特征显示名后追加 `[i]`，与 SolidWorks 行为对齐；
     * 3. 宿主侧显式重命名拥有最高优先级，避免覆盖用户操作。
     *
     * @param input 构建输入。
     * @returns 结果体 ID 到显示名称的映射。
     */
    public buildDisplayNameMap(input: BuildResultBodyDisplayNameMapInput): Map<string, string> {
        const bodyObjects = input.objects.filter((object) => object.type === 'solid');
        const featureById = new Map(
            input.features.map((feature) => [feature.id, feature] as const),
        );
        const featureGroups = this.buildFeatureGroups(bodyObjects);
        const displayNameMap = new Map<string, string>();

        bodyObjects.forEach((object, index) => {
            const overrideName = this.resolveOverrideName(input.objectNameOverrides, object.id);
            if (overrideName) {
                displayNameMap.set(object.id, overrideName);
                return;
            }

            const featureId = this.resolveFeatureId(object);
            const feature = featureId ? featureById.get(featureId) : undefined;
            const featureGroup = featureId ? featureGroups.get(featureId) : undefined;
            const metadataName = this.resolveMetadataName(object);

            if (
                feature
                && featureGroup
                && this.shouldAppendBodyOrdinal(featureGroup)
            ) {
                const ordinal = this.resolveBodyOrdinal(featureGroup, object.id);
                displayNameMap.set(object.id, `${feature.displayName}[${ordinal}]`);
                return;
            }

            if (feature?.displayName) {
                displayNameMap.set(object.id, feature.displayName);
                return;
            }

            if (metadataName) {
                displayNameMap.set(object.id, metadataName);
                return;
            }

            displayNameMap.set(object.id, `实体${index + 1}`);
        });

        return displayNameMap;
    }

    /**
     * 构建按特征分组的结果体上下文。
     *
     * @param bodyObjects 当前结果体对象列表。
     * @returns 特征分组映射。
     */
    private buildFeatureGroups(
        bodyObjects: GeometryObject[],
    ): Map<string, ResultBodyFeatureGroup> {
        const groups = new Map<string, ResultBodyFeatureGroup>();

        bodyObjects.forEach((object) => {
            const featureId = this.resolveFeatureId(object);
            const featureKind = this.resolveFeatureKind(object);
            if (!featureId || !featureKind) {
                return;
            }

            const group = groups.get(featureId) ?? {
                featureId,
                featureKind,
                objectIds: [],
            };
            group.objectIds.push(object.id);
            groups.set(featureId, group);
        });

        groups.forEach((group) => {
            group.objectIds.sort((left, right) => (
                this.resolveFeatureLocalSequence(group.featureId, left)
                - this.resolveFeatureLocalSequence(group.featureId, right)
            ));
        });

        return groups;
    }

    /**
     * 判断当前特征分组是否需要追加结果体序号。
     *
     * @param group 特征分组。
     * @returns 是否需要追加 `[i]`。
     */
    private shouldAppendBodyOrdinal(group: ResultBodyFeatureGroup): boolean {
        if (group.objectIds.length <= 1) {
            return false;
        }
        return group.featureKind === 'boss-extrude'
            || group.featureKind === 'boss-revolve'
            || group.featureKind === 'cut-extrude'
            || group.featureKind === 'cut-revolve';
    }

    /**
     * 解析结果体在同一特征分组中的序号。
     *
     * @param group 特征分组。
     * @param objectId 结果体对象 ID。
     * @returns 从 1 开始的显示序号。
     */
    private resolveBodyOrdinal(group: ResultBodyFeatureGroup, objectId: string): number {
        const sortedIndex = group.objectIds.indexOf(objectId);
        if (sortedIndex >= 0) {
            return sortedIndex + 1;
        }
        return 1;
    }

    /**
     * 解析结果体在同一特征下的局部排序键。
     *
     * @param featureId 特征 ID。
     * @param objectId 结果体对象 ID。
     * @returns 排序键，数值越小越靠前。
     */
    private resolveFeatureLocalSequence(featureId: string, objectId: string): number {
        if (objectId === featureId) {
            return 0;
        }

        const suffix = objectId.startsWith(`${featureId}:`)
            ? objectId.slice(featureId.length + 1)
            : '';
        const parsed = Number(suffix);
        if (Number.isInteger(parsed) && parsed >= 1) {
            return parsed;
        }
        return Number.MAX_SAFE_INTEGER;
    }

    /**
     * 解析宿主侧显式重命名。
     *
     * @param overrides 重命名覆盖表。
     * @param objectId 对象 ID。
     * @returns 修剪后的覆盖名称。
     */
    private resolveOverrideName(overrides: Map<string, string>, objectId: string): string | null {
        const overrideName = (overrides.get(objectId) ?? '').trim();
        return overrideName.length > 0 ? overrideName : null;
    }

    /**
     * 解析结果体来源特征 ID。
     *
     * @param object 结果体对象。
     * @returns 特征 ID。
     */
    private resolveFeatureId(object: GeometryObject): string | null {
        const featureId = object.metadata?.featureId;
        return typeof featureId === 'string' && featureId.length > 0 ? featureId : null;
    }

    /**
     * 解析结果体来源特征种类。
     *
     * @param object 结果体对象。
     * @returns 特征种类。
     */
    private resolveFeatureKind(object: GeometryObject): string | null {
        const featureKind = object.metadata?.featureKind;
        return typeof featureKind === 'string' && featureKind.length > 0 ? featureKind : null;
    }

    /**
     * 解析结果体元数据名称。
     *
     * @param object 结果体对象。
     * @returns 元数据名称。
     */
    private resolveMetadataName(object: GeometryObject): string | null {
        const metadataName = typeof object.metadata?.name === 'string'
            ? object.metadata.name.trim()
            : '';
        return metadataName.length > 0 ? metadataName : null;
    }
}

/**
 * 默认结果体显示名称策略实例。
 */
export const resultBodyDisplayNamePolicy = new ResultBodyDisplayNamePolicy();

/**
 * 构建结果体显示名称映射。
 *
 * @param input 构建输入。
 * @returns 结果体 ID 到显示名称的映射。
 */
export function buildResultBodyDisplayNameMap(
    input: BuildResultBodyDisplayNameMapInput,
): Map<string, string> {
    return resultBodyDisplayNamePolicy.buildDisplayNameMap(input);
}
